import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { type PageDrawer } from 'framework/ViewTypes';
import type {
  FilterBarAction,
  FilterBarFilterConfig,
  FilterBarFiltersProps,
  FilterState,
} from 'framework/ViewTypes/components';
import { SourcingSearchMode, type SourcingFilterOptions, type SourcingFilters } from '../../types';
import SaveSourcingFilterDrawer from './components/SaveSourcingFilterDrawer';
import SavedSourcingFiltersDrawer from './components/SavedSourcingFiltersDrawer';
import SettingsSourcingDrawer from './components/SettingsSourcingDrawer';
import { SOURCING_FILTER_KEYS, SOURCING_FILTER_LABELS, SOURCING_FILTERS } from './constants';
import {
  getSourcingFilterMode,
  mapCollectionFiltersToSourcingFilters,
  mapSourcingParametersToFilters,
} from './utils/sourcingFilterModel';
import { useRecruitmentPoolSourcingFilters } from './useRecruitmentPoolSourcingFilters';
import { getSourcingFilterValue } from './utils/utils';
import SouringTitle from './components/SouringTitle';

type Params = {
  recruitmentPoolId: string;
  filterOptions: SourcingFilterOptions | undefined;
  filters: FilterState;
  onApply: (filters: SourcingFilters) => void;
  canDelete: boolean;
  canSave: boolean;
};

type Result = {
  filterProps?: FilterBarFiltersProps;
  drawer?: PageDrawer[];
};

const isLockedFilter = (key: string) => key === SOURCING_FILTERS.specialization || key === SOURCING_FILTERS.technology;

export const useRecruitmentPoolSourcingFilterBar = ({
  recruitmentPoolId,
  filterOptions,
  filters,
  onApply,
  canDelete,
  canSave,
}: Params): Result => {
  const {
    apply,
    appliedSavedFilterName,
    canSaveFilter,
    handleChipModeToggle,
    matchingSavedFilter,
    onDeleteSavedFilters,
    onOpenSaved,
    onOpenSave,
    onOpenSettings,
    onRemoveFilter,
    reloadSavedFilters,
    saveOpen,
    savedFilters,
    savedFiltersLoaded,
    savedOpen,
    saveSourcingFilter,
    setSaveOpen,
    setSavedOpen,
    setSettingsOpen,
    settingsOpen,
    sourcingFilters,
  } = useRecruitmentPoolSourcingFilters({ recruitmentPoolId, filterOptions, filters, onApply });

  if (!filterOptions) return {};

  const filterConfigs: FilterBarFilterConfig[] = SOURCING_FILTER_KEYS.flatMap(key => {
    const value = getSourcingFilterValue(key, sourcingFilters, filterOptions);
    if (!value) return [];

    return [
      {
        type: 'empty' as const,
        id: key,
        label: SOURCING_FILTER_LABELS[key],
        value,
        strict: getSourcingFilterMode(key, sourcingFilters) === SourcingSearchMode.STRICT,
        onClick: () => handleChipModeToggle(key),
        onRemove: isLockedFilter(key) ? undefined : () => onRemoveFilter(key),
      },
    ];
  });

  const showSave = canSave && savedFiltersLoaded && !matchingSavedFilter && canSaveFilter;

  const actions: FilterBarAction[] = [
    ...((showSave
      ? [
          {
            id: 'save',
            label: 'Save sourcing filter',
            variant: 'text',
            icon: <BookmarkAddOutlinedIcon />,
            onClick: onOpenSave,
          },
        ]
      : []) as FilterBarAction[]),
    { id: 'settings', label: 'Sourcing settings', icon: <ManageSearchIcon />, onClick: onOpenSettings },
    { id: 'saved', label: 'Saved filters', icon: <BookmarkBorderOutlinedIcon />, onClick: onOpenSaved },
  ];

  return {
    filterProps: {
      title: <SouringTitle filterOptions={filterOptions} appliedSavedFilterName={appliedSavedFilterName} />,
      value: filters,
      onChange: nextFilters => apply(mapCollectionFiltersToSourcingFilters(nextFilters)),
      filters: filterConfigs,
      actions,
      sx: {
        gap: 1,
      },
    },
    drawer: [
      <SettingsSourcingDrawer
        key="settings"
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        filterOptions={filterOptions}
        initialFilters={sourcingFilters}
        onApply={nextFilters => apply(nextFilters)}
      />,
      <SaveSourcingFilterDrawer
        key="save"
        isOpen={saveOpen}
        onClose={() => setSaveOpen(false)}
        parameters={sourcingFilters}
        filterOptions={filterOptions}
        onSave={saveSourcingFilter}
      />,
      <SavedSourcingFiltersDrawer
        key="saved"
        isOpen={savedOpen}
        onClose={() => setSavedOpen(false)}
        recruitmentPoolId={recruitmentPoolId}
        filterOptions={filterOptions}
        sourcingFilters={sourcingFilters}
        savedFilters={savedFilters}
        reloadSavedFilters={reloadSavedFilters}
        onApply={saved => apply(mapSourcingParametersToFilters(saved.parameters), true)}
        onDeleted={onDeleteSavedFilters}
        canDelete={canDelete}
      />,
    ],
  };
};
